import type { UserProfile } from '../types/auth.types'

type ProfileCompletionFieldKey =
  | 'firstName'
  | 'lastName'
  | 'dateOfBirth'
  | 'phone'
  | 'avatar'
  | 'verification'
  | 'language'

export interface ProfileCompletionStep {
  key: ProfileCompletionFieldKey
  filled: boolean
  weight: number
  /** i18n key - the human-readable name for the missing field. */
  labelKey: string
  /** Route the CTA should push to so the user can fill the gap. */
  route: string
}

export interface ProfileCompletion {
  /** 0-100. Weighted by business impact (verification = 30%, avatar = 20%, phone = 15%, etc). */
  percent: number
  /** Sorted highest-weight first. Empty when the profile is 100% complete. */
  missingFields: ProfileCompletionStep[]
}

type ProfileCompletionSource = Pick<
  UserProfile,
  | 'firstName'
  | 'lastName'
  | 'dateOfBirth'
  | 'phone'
  | 'avatarUrl'
  | 'isVerified'
  | 'countryId'
  | 'cityId'
  | 'preferredLocale'
>

function hasText(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0
}

export function calculateProfileCompletion(
  profile: ProfileCompletionSource | null | undefined,
): ProfileCompletion {
  if (!profile) return { percent: 0, missingFields: [] }

  const steps: ProfileCompletionStep[] = [
    {
      key: 'firstName',
      filled: hasText(profile.firstName),
      weight: 10,
      labelKey: 'profile.completion.field.first_name',
      route: '/profile/edit-profile',
    },
    {
      key: 'lastName',
      filled: hasText(profile.lastName),
      weight: 10,
      labelKey: 'profile.completion.field.last_name',
      route: '/profile/edit-profile',
    },
    {
      key: 'dateOfBirth',
      filled: hasText(profile.dateOfBirth),
      weight: 10,
      labelKey: 'profile.completion.field.date_of_birth',
      route: '/profile/edit-profile',
    },
    {
      key: 'phone',
      filled: hasText(profile.phone),
      weight: 15,
      labelKey: 'profile.completion.field.phone',
      route: '/profile/edit-profile',
    },
    {
      key: 'avatar',
      filled: hasText(profile.avatarUrl),
      weight: 20,
      labelKey: 'profile.completion.field.avatar',
      route: '/profile/edit-profile',
    },
    {
      key: 'verification',
      filled: profile.isVerified,
      weight: 30,
      labelKey: 'profile.completion.field.verification',
      route: '/profile/verification',
    },
    {
      key: 'language',
      filled: hasText(profile.preferredLocale),
      weight: 5,
      labelKey: 'profile.completion.field.language',
      route: '/profile/edit-profile',
    },
  ]

  const earned = steps.reduce((sum, step) => sum + (step.filled ? step.weight : 0), 0)
  const missingFields = steps.filter((step) => !step.filled).sort((a, b) => b.weight - a.weight)

  return { percent: earned, missingFields }
}

export function isProfileComplete(profile: ProfileCompletionSource | null | undefined): boolean {
  return calculateProfileCompletion(profile).percent === 100
}
